import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { SectionRibbon } from "@/components/AppShell";

/**
 * One file → three lightweight content shells (About, Contact, Policies).
 * The author will fill in real copy later; for now each page renders a
 * friendly placeholder.
 */

const EFFECTIVE_DATE = "March 1, 2026";

const PAGES = {
  about: {
    title: "About TravelBio",
    ribbon: "about us",
    intro: [
      "Travel isn't just about where you go—it's about the stories you collect, the people you share them with, and the memories that stay with you long after you return home.",
      "At TravelBio, we believe every trip becomes part of your life's story.",
      "That's why we created more than a booking platform. We created a place where inspiration, planning, booking, and memory-keeping come together in one beautiful experience.",
      "Whether you're dreaming about your first European adventure, planning your family's annual beach vacation, searching for the perfect honeymoon resort, or building your bucket list one destination at a time, TravelBio helps turn \"someday\" into your next adventure.",
    ],
    blocks: [
      {
        heading: "Discover",
        body: [
          "Finding your next destination should be exciting—not overwhelming.",
          "Explore thousands of destinations through personalized swipeable travel cards, curated collections, powerful filters, and intelligent search. Whether you're looking for a luxury all-inclusive resort, a hidden mountain retreat, a Disney vacation, a romantic escape, or your next cruise, TravelBio helps you discover places you may never have found otherwise.",
        ],
      },
      {
        heading: "Plan",
        body: [
          "Every great trip starts with a dream—and grows with a plan.",
          "Save destinations you love, organize future adventures, compare options, and build personalized travel journals that evolve as your travel goals change. Keep everything in one place instead of scattered across screenshots, browser tabs, notes, and emails.",
        ],
      },
      {
        heading: "Book",
        body: [
          "When you're ready to make it happen, booking should feel effortless.",
          "TravelBio connects you with trusted travel partners so you can reserve accommodations and experiences with confidence while staying focused on the trip you've been planning—not endless searching.",
        ],
      },
      {
        heading: "Remember",
        body: [
          "The vacation may end, but the memories shouldn't.",
          "TravelBio becomes your lifelong travel journal—a place to revisit the places you've explored, celebrate the moments you've shared, and preserve the experiences that shaped your story.",
          "Add photos, memories, notes, favorite restaurants, hidden gems, and personal reflections so every trip becomes part of your own travel legacy.",
        ],
      },
      {
        heading: "Dream Again",
        body: [
          "Every journey inspires the next.",
          "As your TravelBio grows, so does your collection of dreams, experiences, and future adventures. Watch your map fill with destinations you've explored while building an ever-growing bucket list of places still waiting to be discovered.",
          "Because travel isn't about checking destinations off a list.",
          "It's about watching sunsets with the people you love.",
          "It's about celebrating milestones, finding adventure, trying something new, getting lost in beautiful places, and creating stories you'll tell for years to come.",
          "Those moments deserve more than a confirmation email.",
          "They deserve a home.",
        ],
      },
      {
        heading: "Our Mission",
        body:
          "To inspire people to discover the world, simplify the way they plan and book meaningful travel, and preserve every journey as part of their lifelong story.",
      },
      {
        heading: "Our Vision",
        body:
          "To become the world's most loved travel companion—where every dream begins, every adventure is planned, every booking is effortless, and every memory lives forever.",
      },
    ],
    tagline: "Dream it. Plan it. Book it. Cherish it.",
  },
  contact: {
    title: "Contact Us",
    ribbon: "say hello",
    blocks: [
      {
        heading: "Get in touch",
        body: "Questions, partnerships, or feedback? We'd love to hear from you.",
      },
    ],
    email: "info@mytravelbio.com",
  },
  policies: {
    title: "Policies & Disclosures",
    ribbon: "the fine print",
    blocks: [
      {
        heading: "Privacy Policy",
        body: [
          `Effective ${EFFECTIVE_DATE}. TravelBio ("we," "us," or "our") is a personal ` +
            "travel journal and discovery product owned and operated by MyTravelBio LLC. " +
            "This policy explains " +
            "what we collect, how we use it, and the choices you have. By using TravelBio " +
            "(the website at mytravelbio.com and our mobile apps), you agree to this policy.",
          "Information you give us. When you create an account we collect your name, email address, " +
            "and a hashed password. When you log a trip, chapter, journal entry, dream-board pin, or " +
            "lounge post, we store whatever text and images you choose to include. If you use " +
            "\"Continue on your phone,\" we mint a short-lived (60-second) handoff token bound to " +
            "your account.",
          "Information we collect automatically. To keep the service running and secure we log " +
            "basic technical data — IP address, device type, browser, pages viewed, and error events. " +
            "We use first-party session cookies to keep you signed in; the mobile app stores the " +
            "same session tokens in the operating system's secure keychain (never in AsyncStorage " +
            "or unprotected files). Biometric data (Face ID, Touch ID, fingerprint) never leaves your " +
            "device — the OS handles verification and only sends us a yes/no result.",
          "How we use it. To operate the service (authenticate you, sync your journal across " +
            "devices, deliver bookings and rewards), to prevent fraud and abuse, to respond when you " +
            "email us, and to improve the product. We do not sell your personal data. We do not use " +
            "your journal content to train AI models.",
          "Third parties we share with. (1) Emergent, our hosting and AI provider, processes " +
            "requests on our behalf, including automated image moderation. (2) Resend, our email " +
            "delivery partner, sends transactional emails when we roll out the feature. (3) Payment " +
            "processors (Stripe) handle any paid bookings — we never see your full card number. " +
            "(4) Booking affiliates receive the minimum information required to complete a booking " +
            "you initiate. (5) Google, if you sign in with Google, receives standard OAuth data. " +
            "We share with law enforcement only when legally compelled.",
          "Your rights. You can view, edit, export, or delete your account at any time from the " +
            "app. To exercise rights under GDPR (EU/UK) or CCPA/CPRA (California) — including access, " +
            "portability, correction, deletion, or opt-out of \"selling\" or \"sharing\" (we do not " +
            "sell or share for cross-context advertising) — email us at info@mytravelbio.com. We " +
            "respond within 30 days.",
          "Data retention. We keep your account data until you delete it. Deleted accounts are " +
            "purged within 30 days from our live systems and within 90 days from backups. Handoff " +
            "tokens expire in 60 seconds and are irrecoverable once used.",
          "Security. All traffic is encrypted in transit (TLS). Passwords are hashed with bcrypt. " +
            "Session cookies are HTTP-only, Secure, and SameSite-Strict. No system is perfect — if " +
            "we ever have a material breach, we'll notify affected users within 72 hours.",
          "Children. TravelBio is not intended for children under 13 (or under 16 in the EU). We " +
            "do not knowingly collect data from anyone under those ages. If you believe a child has " +
            "signed up, please email us and we'll delete the account.",
          "International transfers. We are based in the United States. If you access TravelBio " +
            "from outside the US, your information will be transferred to and processed in the US.",
          "Changes. We may update this policy. Material changes will be announced in-app or by " +
            "email. Continued use after the effective date of a change means you accept it.",
          "Contact. Questions or requests: info@mytravelbio.com.",
        ],
      },
      {
        heading: "Terms of Service",
        body: [
          `Effective ${EFFECTIVE_DATE}. These terms govern your use of TravelBio.`,
          "Your account. You are responsible for keeping your login credentials secure and for " +
            "everything that happens under your account. You must be at least 13 years old (16 in " +
            "the EU) to use TravelBio.",
          "Your content. Everything you post — journal entries, photos, chapters, lounge posts — " +
            "remains yours. You grant us a non-exclusive license to store, display, and back up " +
            "that content solely so we can run the service for you and, where you've made it public " +
            "(e.g., Lounge, shared chapters), display it to the audience you chose. You can revoke " +
            "this license at any time by deleting the content or your account.",
          "Community rules. Don't post hateful, harassing, sexually explicit, or illegal content. " +
            "Don't impersonate others. Don't scrape or reverse-engineer the service. Automated " +
            "image moderation may flag or remove content in obvious cases; human review handles " +
            "edge cases. Repeat violations may result in account suspension.",
          "Bookings and affiliates. TravelBio is not a travel agency. When you book through us, " +
            "the booking contract is between you and the third-party provider. MyTravelBio LLC may earn a " +
            "commission from that provider — this does not change the price you pay.",
          "Rewards and passport points. Points, stamps, and rewards have no cash value, are " +
            "non-transferable, and may be modified or discontinued at any time.",
          "Termination. You can delete your account at any time. We can suspend or terminate " +
            "accounts that violate these terms or the law.",
          "Disclaimer. The service is provided \"as is\" without warranties of any kind. We do " +
            "not guarantee uninterrupted or error-free operation. To the maximum extent permitted " +
            "by law, MyTravelBio LLC's liability is limited to the amount you paid us in the 12 months " +
            "preceding the claim (or, if you paid nothing, USD $100).",
          "Governing law. These terms are governed by the laws of the United States and, to the " +
            "extent applicable, the state in which MyTravelBio LLC is registered. Any dispute will be " +
            "resolved in courts of competent jurisdiction there.",
          "Contact. info@mytravelbio.com.",
        ],
      },
      {
        heading: "Affiliate Disclosure",
        body: [
          "TravelBio may earn a small commission when you book flights, hotels, tours, or " +
            "activities through partner links or the in-app booking flow. This commission is paid " +
            "by the partner — not by you — and does not change the price you'd pay booking " +
            "directly with them.",
          "We only feature partners we believe offer real value to travelers. We are never paid " +
            "to feature specific destinations, and search results are ranked by relevance, not by " +
            "commission rate.",
        ],
      },
      {
        heading: "Cookies",
        body:
          "TravelBio uses a single first-party session cookie to keep you signed in. We do not " +
          "use third-party advertising cookies or cross-site trackers. Signing out clears the cookie.",
      },
    ],
  },
};

function Shell({ slug }) {
  const nav = useNavigate();
  const cfg = PAGES[slug];
  if (!cfg) return null;
  return (
    <div data-testid={`info-${slug}-page`} className="flex flex-col">
      <SectionRibbon>{cfg.ribbon}</SectionRibbon>

      <div className="px-5 pt-3 pb-2">
        <button
          onClick={() => nav(-1)}
          aria-label="Back"
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>
      </div>

      <article className="px-5 pb-10 lg:px-10 lg:max-w-3xl lg:mx-auto w-full">
        <h1 className="font-serif-it text-tb-navy text-4xl mb-6 leading-tight">
          {cfg.title}
        </h1>

        {/* Optional intro paragraphs (About page only) — rendered above the
            first section heading. Body is measured against ~65ch for
            comfortable reading on desktop. */}
        {Array.isArray(cfg.intro) && cfg.intro.length > 0 && (
          <div className="mb-8 max-w-[65ch]">
            {cfg.intro.map((p, i) => (
              <p
                key={i}
                className="font-ui text-tb-navy/85 leading-relaxed mb-3 last:mb-0 text-base"
              >
                {p}
              </p>
            ))}
          </div>
        )}

        {cfg.blocks.map((b) => (
          <section key={b.heading} className="mb-8 max-w-[65ch]">
            <h2 className="font-serif-it text-tb-navy text-2xl font-bold mb-3" style={{ color: "#1a3a5f" }}>{b.heading}</h2>
            {Array.isArray(b.body) ? (
              b.body.map((p, i) => (
                <p
                  key={i}
                  className="font-ui text-tb-navy/85 leading-relaxed mb-3 last:mb-0"
                >
                  {p}
                </p>
              ))
            ) : (
              <p className="font-ui text-tb-navy/85 leading-relaxed">{b.body}</p>
            )}
          </section>
        ))}

        {/* Optional tagline (About page only) — visually prominent card. */}
        {cfg.tagline && (
          <div className="my-8 mx-auto max-w-[65ch] rounded-2xl border-2 border-tb-gold bg-tb-cream-2/60 px-6 py-6 text-center">
            <p className="font-serif-it text-tb-navy text-2xl italic leading-snug" style={{ color: "#1a3a5f" }}>
              {cfg.tagline}
            </p>
          </div>
        )}

        {cfg.email && (
          <a
            href={`mailto:${cfg.email}`}
            data-testid="contact-email-link"
            className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:brightness-110"
          >
            <Mail className="w-4 h-4" />
            {cfg.email}
          </a>
        )}

        {/* Advisor card — Contact page only. 2026-07-18. Purely
            informational, no CTA buttons. Rendered AFTER the
            site-wide info@ button per 2026-07-18 reorder. */}
        {slug === "contact" && (
          <section
            data-testid="about-advisor-card"
            className="my-8 mx-auto max-w-[65ch] rounded-2xl border border-tb-navy/15 bg-white p-6 md:p-8 shadow-sm"
          >
            <h2 className="font-serif-it text-tb-navy text-2xl md:text-3xl leading-snug">
              Contact a Travel Advisor
            </h2>
            <div className="mt-4">
              <p className="font-serif text-tb-navy text-xl md:text-2xl leading-tight">
                Ashley Root
              </p>
              <p className="font-ui text-tb-navy/60 text-sm mt-0.5">
                MyTravelBio LLC
              </p>
            </div>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-2">
              <dt className="font-ui text-tb-navy/60 text-sm">CLIA Personal ID</dt>
              <dd className="font-ui text-tb-navy text-sm sm:text-right tabular-nums">00682284</dd>

              <dt className="font-ui text-tb-navy/60 text-sm">CLIA Company/Agency Industry ID</dt>
              <dd className="font-ui text-tb-navy text-sm sm:text-right tabular-nums">00736285</dd>

              <dt className="font-ui text-tb-navy/60 text-sm">Membership Expires</dt>
              <dd className="font-ui text-tb-navy text-sm sm:text-right tabular-nums">2026/12/31</dd>
            </dl>

            <div className="my-5 h-px bg-tb-navy/15" role="separator" />

            <ul className="flex flex-col gap-2">
              <li>
                <a
                  data-testid="about-advisor-email"
                  href="mailto:ashleyroot@mytravelbio.com"
                  className="inline-flex items-center gap-2 font-ui text-sm md:text-base text-tb-navy hover:underline"
                >
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  ashleyroot@mytravelbio.com
                </a>
              </li>
              <li>
                <a
                  data-testid="about-advisor-phone"
                  href="tel:+18022305597"
                  className="inline-flex items-center gap-2 font-ui text-sm md:text-base text-tb-navy hover:underline"
                >
                  <Phone className="w-4 h-4" aria-hidden="true" />
                  802-230-5597
                </a>
              </li>
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}

/** Helper to pick the right page from a single component (smaller bundle than
 *  exporting three separate identical wrappers). */
export default function InfoPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0]; // "about" | "contact" | "policies"
  // Policies is now a list of 10 documents; each links to its own detail page.
  if (slug === "policies") return <PoliciesList />;
  return <Shell slug={slug} />;
}

// Lightweight new-style Policies index — matches the existing Shell layout
// so it visually blends with About/Contact.
import { POLICIES } from "@/content/policies";
import { ChevronRight } from "lucide-react";
function PoliciesList() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="policies-list">
      <p className="font-hand italic text-tb-muted text-xs uppercase tracking-widest mb-2">
        the fine print
      </p>
      <h1 className="font-serif text-4xl text-tb-navy mb-6">
        Policies &amp; Disclosures
      </h1>
      <div className="divide-y divide-tb-cream-3 rounded-2xl border border-tb-cream-3 overflow-hidden bg-white/60">
        {POLICIES.map((p) => (
          <Link
            key={p.slug}
            to={`/policies/${p.slug}`}
            data-testid={`policies-row-${p.slug}`}
            className="flex items-center justify-between px-5 py-4 hover:bg-tb-cream/50 transition-colors"
          >
            <span className="font-serif text-base text-tb-navy">{p.title}</span>
            <ChevronRight className="w-4 h-4 text-tb-muted" />
          </Link>
        ))}
      </div>
    </div>
  );
}
